import React, { useMemo } from 'react';

import { NewsTickerItemDataFull } from '@/models/feed';
import { Badge, Title } from '@/models/profile';
import { setLangIso } from '@/state/features/app';
import store, { useAppDispatch, useAppSelector } from '@/state/store';
import { DEFAULT_LANG } from '@/globals';

interface TranslatedTextProps {
    translationKey: string;
    fallback?: string;
}

function TranslatedText({ translationKey, fallback }: TranslatedTextProps) {
    // Implementation will be added later
    return <>{fallback || translationKey}</>;
}

type UITranslationKeys =
    // General
    | 'title'
    | 'share'
    | 'copy'
    | 'copied'
    | 'loading'
    // Profile Drawer
    | 'profileDrawer_quest'
    | 'profileDrawer_mission'
    | 'profileDrawer_scorecard'
    | 'profileDrawer_titles'
    | 'profileDrawer_level'
    | 'profileDrawer_yourLevel'
    | 'profileDrawer_incomplete'
    | 'profileDrawer_complete'
    | 'profileDrawer_titlesUnlocked'
    | 'profileDrawer_yourStats'
    | 'profileDrawer_levels'
    | 'profileDrawer_shareError'
    | 'profileDrawer_titleNotFound'
    | 'profileDrawer_hint'
    | 'profileDrawer_logoImage'
    // Loading Screen
    | 'loading_message1Title'
    | 'loading_message1Subtitle'
    | 'loading_message2Title'
    | 'loading_message2Subtitle'
    | 'loading_message3Title'
    | 'loading_message3Subtitle'
    // Information Screen
    | 'informationScreen_introText'
    | 'informationScreen_mainText'
    | 'informationScreen_topForegroundImageAltText'
    | 'informationScreen_poweredBy'
    | 'informationScreen_shareText'
    | 'informationScreen_additionalFooterText'
    | 'informationScreen_topForegroundImage'
    // Legal
    | 'legal_privacyPolicy'
    | 'legal_privacyChoices'
    | 'legal_californiaNotice'
    | 'legal_filmRatings'
    | 'legal_termsOfUse'
    | 'legal_mpa'
    // Notifications
    | 'notification_badgeActionPrefix'
    | 'notification_titleActionPrefix'
    | 'notification_questCompleteActionPrefix'
    | 'notification_missionCompleteActionPrefix'
    // Rarities
    | 'rarity_common'
    | 'rarity_uncommon'
    | 'rarity_rare'
    | 'rarity_epic'
    | 'rarity_legendary';

interface TranslatedUITextProps {
    translationKey: UITranslationKeys;
    fallback?: string;
}

export function TranslatedUIText({ translationKey, fallback }: TranslatedUITextProps) {
    const translation = useUITranslatedText(translationKey, fallback);
    return <>{translation}</>;
}

export default TranslatedText;

// Core translation lookup logic
function getTranslation(
    translations: Record<string, Record<string, string>> | undefined,
    langIso: string | undefined,
    translationKey: string,
    fallback?: string,
): string {
    const translation = translations?.[langIso]?.[translationKey];

    if (translation) {
        return translation;
    }

    if (translations?.[langIso] && process.env.PUBLIC_SUPPORT_TRANSLATIONS === 'true') {
        console.warn(`Translation for key "${translationKey}" not found in language "${langIso}". Using fallback.`);
    }

    return fallback || '';
}

export function useTranslatedText(translationKey: string, fallback?: string) {
    const langIso = useAppSelector((state) => state.app.langIso);
    const translations = useAppSelector((state) => state.app.translations);
    return getTranslation(translations, langIso, translationKey, fallback);
}

export function useUITranslatedText(key: UITranslationKeys, fallback?: string) {
    const universeId = useAppSelector((state) => state.app.universeId);
    const fullKey = `ui-${universeId}__${key}`;
    return useTranslatedText(fullKey, fallback);
}

export function useCFTranslatedText(key: string, fallback?: string) {
    const universeId = useAppSelector((state) => state.app.universeId);
    const fullKey = `cf-${universeId}__${key}`;
    return useTranslatedText(fullKey, fallback);
}

// Non-hook version for use outside React components (e.g. Redux thunks)
export function getUITranslatedText(key: string, fallback: string): string {
    const state = store.getState();
    const { langIso, translations, universeId } = state.app;
    const fullKey = `ui-${universeId}__${key}`;
    return getTranslation(translations, langIso, fullKey, fallback);
}

export function useLang() {
    const current = useAppSelector((state) => state.app.langIso);
    const availableLangs = useAppSelector((state) => state.app.availableLangs) ?? [];

    // Put default lang to the beginning if it exists
    const langs = useMemo(
        () => [
            ...availableLangs.filter((l) => l.langISO?.toLowerCase() === DEFAULT_LANG.langISO),
            ...availableLangs.filter((l) => l.langISO?.toLowerCase() !== DEFAULT_LANG.langISO),
        ],
        [availableLangs],
    );

    const dispatch = useAppDispatch();

    function setLang(langIso: string) {
        if (process.env.PUBLIC_SUPPORT_TRANSLATIONS !== 'true') {
            // setLang ignored as translations are disabled
            return;
        }
        dispatch(setLangIso(langIso));
    }

    return {
        current,
        langs,
        setLang,
    };
}

// Title translations
// Keys are in format: title-{id}__{field}
// e.g. title-chef__title, title-chef__description, title-chef__hint
export function getTitleTranslatedText(titleId: string, field: string, fallback: string): string {
    const state = store.getState();
    const { langIso, translations } = state.app;
    const fullKey = `title-${titleId}__${field}`;
    return getTranslation(translations, langIso, fullKey, fallback);
}

// Badge translations
// Keys are in format: badge-{id}__{field}
// e.g. badge-2f5f4c9f-66ee-49f0-9591-66200eabbaa0__description
export function getBadgeTranslatedText(badgeId: string, field: string, fallback: string): string {
    const state = store.getState();
    const { langIso, translations } = state.app;
    const fullKey = `badge-${badgeId}__${field}`;
    return getTranslation(translations, langIso, fullKey, fallback);
}

// NewsTicker translations
// Keys are in format: newsticker-{uuid}__{field}
// e.g. newsticker-0300dddd-f03c-4acd-9e54-382f6effb734__recentEventsMessageTemplate
export function getNewsTickerTranslatedText(uuid: string, field: string, fallback: string): string {
    const state = store.getState();
    const { langIso, translations } = state.app;
    const fullKey = `newsticker-${uuid}__${field}`;
    return getTranslation(translations, langIso, fullKey, fallback);
}

export function getTranslatedBadge(badge: Badge): Badge {
    if (!badge) return;
    const key = badge?.id;
    if (!key) {
        console.log(`Cannot translate badge as id missing: ${badge}`);
        return badge;
    }
    return {
        ...badge,
        name: getBadgeTranslatedText(key, 'name', badge.name),
        description: getBadgeTranslatedText(key, 'description', badge.description),
    };
}

export function getTranslatedTitle(title: Title): Title {
    if (!title) return;
    const key = title?.id;
    if (!key) {
        console.log(`Cannot translate title as id missing: ${title}`);
        return title;
    }
    return {
        ...title,
        title: getTitleTranslatedText(key, 'title', title.title),
        description: getTitleTranslatedText(key, 'description', title.description),
        hint: getTitleTranslatedText(key, 'hint', title.hint || title.description),
    };
}

export function getTranslatedNewsTickerItem(
    item: NewsTickerItemDataFull,
    type: 'personal' | 'recent',
): NewsTickerItemDataFull {
    if (!item) return;
    const key = item?.uuid;
    if (!key) {
        console.log(`Cannot translate newsTickerItem as uuid missing: ${item}`);
        return item;
    }
    const field = type === 'recent' ? 'recentEventsMessageTemplate' : 'userMessageTemplate';
    return {
        ...item,
        message: getNewsTickerTranslatedText(key, field, item.message),
    };
}
